import React, { useState, useMemo } from 'react';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { 
  useFetchCryptoWallets,
  useFetchCryptoHoldings,
  useFetchCryptoPrices,
  useDeleteCryptoWallet,
  useCryptoHoldingsAggregation,
} from '@cryptoHooks';
import { CryptoPortfolioOverview } from './components/CryptoPortfolioOverview';
import { CryptoHoldingList } from './components/CryptoHoldingList';
import { WalletDialog } from '@/components/crypto-wallet-dialog/WalletDialog';
import { HoldingDialog } from '@/components/crypto-holding-dialog/HoldingDialog';
import { Button } from '@/components/ui/button';
import { Plus, Wallet, RefreshCw, ArrowLeftRight, AlertTriangle, Trash2, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {CryptoWallet} from "@/integrations/supabase/crypto.models.ts";

// TODO: fetch currencies from API
const CURRENCIES = ['USD', 'EUR', 'GBP', 'KES', 'BTC', 'ETH'];

export default function CryptoPage() {
  const [baseCurrency, setBaseCurrency] = useState('USD');
  const [isWalletDialogOpen, setIsWalletDialogOpen] = useState(false);
  const [isHoldingDialogOpen, setIsHoldingDialogOpen] = useState(false);
  const [deletingWallet, setDeletingWallet] = useState<CryptoWallet | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const { data: wallets = [], isLoading: walletsLoading, error: walletsError, refetch: refetchWallets } = useFetchCryptoWallets();
  const { data: holdings = [], isLoading: holdingsLoading, error: holdingsError, refetch: refetchHoldings } = useFetchCryptoHoldings();

  const filteredHoldings = useMemo(() => {
    if (!searchQuery) return holdings;
    const lowerQuery = searchQuery.toLowerCase();
    return holdings.filter(h => {
      const walletName = wallets.find(w => w.id === h.wallet_id)?.name?.toLowerCase() || '';
      return h.symbol?.toLowerCase().includes(lowerQuery) ||
             h.name?.toLowerCase().includes(lowerQuery) ||
             walletName.includes(lowerQuery);
    });
  }, [holdings, searchQuery, wallets]);

  const symbols = useMemo(() => Array.from(new Set(filteredHoldings.map(h => h.symbol))), [filteredHoldings]);
  const { data: prices = [], isLoading: pricesLoading, error: pricesError, refetch: refetchPrices } = useFetchCryptoPrices(symbols);

  const aggregatedHoldings = useMemo(() => useCryptoHoldingsAggregation(filteredHoldings), [filteredHoldings]);

  const deleteWallet = useDeleteCryptoWallet();

  const handleRefresh = () => {
    refetchHoldings();
    refetchPrices();
    toast.info('Refreshing crypto data...');
  };

  const handleConfirmDeleteWallet = async () => {
    if (!deletingWallet) return;
    try {
      await deleteWallet.mutateAsync(deletingWallet.id);
      setDeletingWallet(null);
    } catch {
      toast.error('Failed to delete wallet. Please try again.');
    }
  };

  return (
    <ErrorBoundary onReset={() => window.location.reload()}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Crypto Portfolio</h1>
            <p className="text-sm text-muted-foreground sm:text-base">
              Track your cryptocurrency holdings across various providers
            </p>
          </div>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:flex-wrap w-full lg:w-auto mt-4 lg:mt-0">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search holdings..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <Select value={baseCurrency} onValueChange={setBaseCurrency}>
              <SelectTrigger className="w-[100px]">
                <ArrowLeftRight className="mr-2 h-4 w-4" />
                <SelectValue placeholder="Currency" />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map(c => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={handleRefresh}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Button variant="outline" onClick={() => setIsWalletDialogOpen(true)}>
              <Wallet className="mr-2 h-4 w-4" />
              Add Wallet
            </Button>
              <Button onClick={() => setIsHoldingDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Holding
              </Button>
            </div>
          </div>
        </div>

        {/* Price error warning banner (non-blocking) */}
        {pricesError && (
          <Alert className="border-yellow-200 bg-yellow-50 text-yellow-800 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-200">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Price data may be stale — live prices are currently unavailable.
            </AlertDescription>
          </Alert>
        )}

        {/* Overview Cards */}
        <CryptoPortfolioOverview 
          aggregatedHoldings={aggregatedHoldings}
          prices={prices}
          pricesLoading={pricesLoading}
          baseCurrency={baseCurrency} 
        />

        {/* Wallets Section */}
        {walletsLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-6 w-32" />
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <Skeleton className="h-20 rounded-lg" />
              <Skeleton className="h-20 rounded-lg" />
              <Skeleton className="h-20 rounded-lg" />
            </div>
          </div>
        ) : walletsError ? (
          <Card className="border-destructive/50">
            <CardContent className="flex items-center justify-between p-4">
              <p className="text-sm text-destructive">Failed to load wallets.</p>
              <Button variant="outline" size="sm" onClick={() => refetchWallets()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : wallets.length > 0 ? (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold">Your Wallets</h3>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {wallets.map((wallet) => (
                <div
                  key={wallet.id}
                  className="flex items-center justify-between rounded-lg border bg-card p-4"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Wallet className="h-5 w-5 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <p className="font-medium truncate">{wallet.name}</p>
                      <p className="text-sm text-muted-foreground truncate">{wallet.provider_name}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={() => setDeletingWallet(wallet)}
                    aria-label={`Delete wallet ${wallet.name}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Holdings Section */}
        {holdingsLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-24 rounded-lg" />
            <Skeleton className="h-24 rounded-lg" />
          </div>
        ) : holdingsError ? (
          <Card className="border-destructive/50">
            <CardContent className="flex items-center justify-between p-4">
              <p className="text-sm text-destructive">Failed to load holdings.</p>
              <Button variant="outline" size="sm" onClick={() => refetchHoldings()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : (
          <CryptoHoldingList 
            holdings={filteredHoldings} 
            aggregatedHoldings={aggregatedHoldings}
            prices={prices}
            pricesLoading={pricesLoading}
            baseCurrency={baseCurrency}
            wallets={wallets}
          />
        )}

        {/* Wallet Dialog */}
        <WalletDialog
          open={isWalletDialogOpen}
          onOpenChange={setIsWalletDialogOpen}
          onSuccess={() => setIsWalletDialogOpen(false)}
        />

        {/* Holding Dialog */}
        <HoldingDialog
          open={isHoldingDialogOpen}
          onOpenChange={setIsHoldingDialogOpen}
          wallets={wallets}
          onSuccess={() => setIsHoldingDialogOpen(false)}
        />

        {/* Delete Wallet Confirmation Dialog */}
        <AlertDialog open={!!deletingWallet} onOpenChange={(open) => { if (!open) setDeletingWallet(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Wallet</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong>{deletingWallet?.name}</strong> ({deletingWallet?.provider_name})?
                This will permanently delete the wallet and ALL holdings within it. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDeleteWallet}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete Wallet
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </ErrorBoundary>
  );
}
